import {
  DeliveryForecastDespatchAdvicePackagingItem,
  PackagingItemHierarchyRestrictions,
  getPackagingItemHierarchyRestrictions,
} from '../../../data/models'
import i18n from '../../../utils/i18n'

export interface ParentOption {
  code: string
  name: string
}
export type ParentOptions = ParentOption[]

/**
 * Retrieves parent options based on the provided items and packaging type.
 *
 * @param items - An array of packaging items to filter.
 * @param packagingType - The type of packaging to check against allowed parent types.
 * @returns An array of ParentOptions that match the allowed parent types.
 */
export function getParentOptions(
  items: DeliveryForecastDespatchAdvicePackagingItem[],
  packagingType: DeliveryForecastDespatchAdvicePackagingItem['PackagingType'],
): ParentOptions {
  if (!items || !packagingType) return []

  const { allowedParentTypes } =
    getPackagingItemHierarchyRestrictions(packagingType)

  if (!allowedParentTypes) return []

  let parentOptions: ParentOptions = []

  items.forEach(item => {
    if (isParent(item.PackagingType, allowedParentTypes)) {
      parentOptions.push(createParentOption(item))
    }
    // Recursively check child items
    if (item.Items && item.Items.length > 0) {
      parentOptions = parentOptions.concat(
        getParentOptions(item.Items, packagingType),
      )
    }
  })

  return parentOptions
}

/**
 * Checks if a given package type is considered a parent based on allowed parent types.
 *
 * @param packageType - The packaging type to check.
 * @param allowedParentTypes - The list of allowed parent types.
 * @returns True if the package type is a parent, otherwise false.
 */
const isParent = (
  packageType: DeliveryForecastDespatchAdvicePackagingItem['PackagingType'],
  allowedParentTypes: PackagingItemHierarchyRestrictions['allowedParentTypes'],
) => {
  if (allowedParentTypes?.includes(packageType)) return true

  return false
}

/**
 * Creates a ParentOption from a given packaging item.
 *
 * @param parent - The packaging item to convert into a ParentOption.
 * @returns A ParentOption object with code and name.
 */
const createParentOption = (
  parent: DeliveryForecastDespatchAdvicePackagingItem,
): ParentOption => {
  const { t } = i18n

  return {
    code: parent.Id!,
    name: t(
      'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.dialog.form.selectMaster.options.pos',
      {
        position: parent.Position,
        description: parent.PackagingData.Description,
      },
    ),
  }
}
